import type React from 'react';
import { Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import PartnerRoute from '../../../route/PartnerRoute/PartnerRoute';
import UserRoute from '../../../route/UserRoute/UserRoute';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import AppointmentDetailsPage from '../../page/AppointmentDetails/AppointmentDetailsPage';
import AppointmentsPage from '../../page/Appointments/AppointmentsPage';
import Confirm from '../../page/Confirm';
import CookiePolicy from '../../page/CookiePolicy';
import DashboardPage from '../../page/Dashboard/DashboardPage';
import Home from '../../page/Home/Home';
import Login from '../../page/Login';
import NotFound from '../../page/NotFound';
import OneClickAction from '../../page/OneClickAction/OneClickAction';
import PartnerInReview from '../../page/PartnerInReview/PartnerInReview';
import PartnerProfile from '../../page/PartnerProfile/PartnerProfile';
import Prices from '../../page/Prices/Prices';
import PrivacyPolicy from '../../page/PrivacyPolicy';
import Register from '../../page/Register';
import ResetPassword from '../../page/ResetPassword';
import Services from '../../page/Services/Services';
import TermsAndConditions from '../../page/TermsAndConditions';
import Venues from '../../page/Venues/Venues';

const Account = lazyReactNaiveRetry(() => import('../../page/Account/Account'));
const Messages = lazyReactNaiveRetry(
  () => import('../../page/Messages/Messages'),
);

interface AppRoutesProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

export const generateRouteElements = (
  route: string,
  element: React.ReactNode | null,
  ssrFetchedData: AppSsrFetchedDataType,
) => {
  const supportedLanguages = ssrFetchedData.appConfiguration.languages;

  return (
    <>
      <Route path={route} element={element} />
      {supportedLanguages.map((supportedLanguage) => {
        return (
          <Route
            key={`/${supportedLanguage}${route}`}
            path={`/${supportedLanguage}${route}`}
            element={element}
          />
        );
      })}
    </>
  );
};

const AppRoutes: React.FC<AppRoutesProps> = ({ ssrFetchedData }) => {
  return (
    <Routes>
      {generateRouteElements(
        ROUTES.HOME_ROUTE,
        <Home ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.SEARCH_ROUTE,
        <Home ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.PRICES_ROUTE,
        <Prices ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.ACCOUNT_ROUTE,
        <UserRoute>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <Account ssrFetchedData={ssrFetchedData} />
          </Suspense>
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.TERMS_AND_CONDITIONS_ROUTE,
        <TermsAndConditions ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.PRIVACY_POLICY_ROUTE,
        <PrivacyPolicy ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.COOKIE_POLICY_ROUTE,
        <CookiePolicy ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.CONFIRM_ACCOUNT_ROUTE,
        <Confirm ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.RESET_PASSWORD_ROUTE,
        <ResetPassword ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.ONE_CLICK_ACTION_ROUTE,
        <OneClickAction ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.SIGNIN_ROUTE,
        <Login ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.SIGNUP_ROUTE,
        <Register ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.NOT_FOUND_ROUTE,
        <NotFound ssrFetchedData={ssrFetchedData} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.PARTNER_INREVIEW_ROUTE,
        <UserRoute>
          <PartnerInReview ssrFetchedData={ssrFetchedData} />
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.PARTNER_PUBLIC_PROFILE_ROUTE,
        <PartnerProfile ssrFetchedData={ssrFetchedData} byPath={false} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.PARTNER_PROFILE_ROUTE,
        <PartnerProfile ssrFetchedData={ssrFetchedData} byPath={true} />,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.MESSAGES_ROUTE,
        <UserRoute>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <Messages ssrFetchedData={ssrFetchedData} />
          </Suspense>
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.CONVERSATION_ROUTE,
        <UserRoute>
          <Suspense fallback={<div className="hidden">&nbsp;</div>}>
            <Messages ssrFetchedData={ssrFetchedData} />
          </Suspense>
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.APPOINTMENTS_ROUTE,
        <UserRoute>
          <AppointmentsPage ssrFetchedData={ssrFetchedData} />
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.APPOINTMENT_DETAILS_ROUTE,
        <UserRoute>
          <AppointmentDetailsPage ssrFetchedData={ssrFetchedData} />
        </UserRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.SERVICES_ROUTE,
        <PartnerRoute>
          <Services ssrFetchedData={ssrFetchedData} />
        </PartnerRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.VENUES_ROUTE,
        <PartnerRoute>
          <Venues ssrFetchedData={ssrFetchedData} />
        </PartnerRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        ROUTES.DASHBOARD_ROUTE,
        <PartnerRoute>
          <DashboardPage ssrFetchedData={ssrFetchedData} />
        </PartnerRoute>,
        ssrFetchedData,
      )}
      {generateRouteElements(
        `${ROUTES.DASHBOARD_ROUTE}/*`,
        <PartnerRoute>
          <DashboardPage ssrFetchedData={ssrFetchedData} />
        </PartnerRoute>,
        ssrFetchedData,
      )}
      <Route path="*" element={<NotFound ssrFetchedData={ssrFetchedData} />} />
    </Routes>
  );
};

export default AppRoutes;
